import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { CheckCircle2, Clock, XCircle, ListChecks, History, Users, AlertCircle } from 'lucide-react';
import { adminApi } from '@/lib/adminApi';
import { useSort } from '@/lib/useSort';
import { SortableHeader } from '@/components/shared/SortableHeader';
import { useDataLabel } from '@/lib/dataI18n';

const STATUS_META = {
  pending:     { label: 'decisions.statuses.pending',     icon: Clock,        tone: 'bg-warning/10 text-warning border-warning/30' },
  in_progress: { label: 'decisions.statuses.in_progress', icon: ListChecks,   tone: 'bg-info/10 text-info border-info/30' },
  done:        { label: 'decisions.statuses.done',        icon: CheckCircle2, tone: 'bg-success/10 text-success border-success/30' },
  dismissed:   { label: 'decisions.statuses.dismissed',   icon: XCircle,      tone: 'bg-muted text-muted-foreground border-border' },
};

export default function AdminDecisions() {
  const { t, i18n } = useTranslation();
  const tData = useDataLabel();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [assignees, setAssignees] = useState([]);
  const [actions, setActions] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(null);
  const [updateNote, setUpdateNote] = useState('');
  const [updateStatus, setUpdateStatus] = useState('');
  const [updateAssignee, setUpdateAssignee] = useState('');
  const [updateDueDate, setUpdateDueDate] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const cos = await adminApi.listCompanies();
        setCompanies(cos);
        if (cos.length) setCompanyId(cos[0].id);
      } catch { toast.error(t('toasts.failedToLoadCompanies')); }
    })();
  }, []);

  const reload = async (cid = companyId) => {
    if (!cid) return;
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const [data, ass] = await Promise.all([
        adminApi.listActions(cid, params),
        adminApi.listAssignees(cid),
      ]);
      setActions(data);
      setAssignees(ass);
    } catch (e) { toast.error(t('toasts.failedToLoadDecisions')); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [companyId, statusFilter]);

  const submitUpdate = async () => {
    try {
      const body = {};
      if (updateStatus && updateStatus !== open.status) body.status = updateStatus;
      if (updateNote) body.notes = updateNote;
      if (updateAssignee && updateAssignee !== (open.assignee_user_id || '')) {
        if (updateAssignee === '__none__') body.clear_assignee = true;
        else body.assignee_user_id = updateAssignee;
      }
      if (updateDueDate !== '') {
        if (updateDueDate === '__clear__') body.clear_due_date = true;
        else body.due_date = new Date(updateDueDate).toISOString();
      }
      if (!Object.keys(body).length) { setOpen(null); return; }
      await adminApi.updateAction(companyId, open.id, body);
      toast.success(t('common.saved', { defaultValue: 'Saved' }));
      setOpen(null); setUpdateNote(''); setUpdateStatus(''); setUpdateAssignee(''); setUpdateDueDate('');
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const today = new Date();
  const isOverdue = (a) => a.due_date && new Date(a.due_date) < today && !['done','dismissed'].includes(a.status);

  const visible = useMemo(() => {
    const q = (search || '').trim().toLowerCase();
    if (!q) return actions;
    return actions.filter(a =>
      (a.suggestion_title || '').toLowerCase().includes(q) ||
      (a.user?.name || '').toLowerCase().includes(q) ||
      (a.suggestion_type || '').toLowerCase().includes(q) ||
      (a.assignee_name || '').toLowerCase().includes(q),
    );
  }, [actions, search]);

  const sort = useSort('created', 'desc');
  const sorted = useMemo(() => sort.apply(visible, {
    created:  (a) => a.created_at ? new Date(a.created_at).getTime() : 0,
    employee: (a) => (a.user?.name || '').toLowerCase(),
    action:   (a) => (a.suggestion_title || '').toLowerCase(),
    status:   (a) => (a.status || '').toLowerCase(),
    assignee: (a) => (a.assignee_name || '').toLowerCase(),
    due:      (a) => a.due_date ? new Date(a.due_date).getTime() : Number.MAX_SAFE_INTEGER,
    steps:    (a) => (a.audit_trail || []).length,
  }), [visible, sort]);

  const fmtDate = (s) => s ? new Date(s).toLocaleDateString(i18n.language, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
  const fmtTime = (s) => s ? new Date(s).toLocaleString(i18n.language, { dateStyle: 'short', timeStyle: 'short' }) : '—';

  const stats = useMemo(() => {
    const c = { pending: 0, in_progress: 0, done: 0, dismissed: 0 };
    actions.forEach(a => { c[a.status] = (c[a.status] || 0) + 1; });
    return c;
  }, [actions]);

  return (
    <>
      <Topbar
        title={t('decisions.title', { defaultValue: 'Decisions log' })}
        subtitle={t('decisions.subtitle', { defaultValue: 'Track every action triggered by TalentIntelligence with full audit trail' })}
        actions={
          <div className="flex items-center gap-2">
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger className="w-56" data-testid="dec-company-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        }
      />

      <div className="p-6 lg:p-8 space-y-6">
        {/* KPI strip */}
        <div className="grid gap-4 md:grid-cols-4">
          {Object.entries(STATUS_META).map(([k, m]) => (
            <Card key={k} className="p-5 shadow-card" data-testid={`dec-kpi-${k}`}>
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t(`decisions.${k}`, { defaultValue: m.label })}</p>
                <m.icon className="h-4 w-4 text-muted-foreground" />
              </div>
              <p className="font-display text-2xl font-bold tabular-nums mt-2">{stats[k] || 0}</p>
            </Card>
          ))}
        </div>

        {/* Filters + table */}
        <Card className="p-6 shadow-card">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <Input value={search} onChange={(e)=>setSearch(e.target.value)} placeholder={t('decisions.searchPh', { defaultValue: 'Search by employee or action…' })} className="max-w-xs" data-testid="dec-search" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-44" data-testid="dec-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all', { defaultValue: 'All' })}</SelectItem>
                {Object.entries(STATUS_META).map(([k, m]) => <SelectItem key={k} value={k}>{t(`decisions.${k}`, { defaultValue: m.label })}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b">
                  <th className="py-2.5 pr-3"><SortableHeader sort={sort} k="created">{t('decisions.created', { defaultValue: 'Created' })}</SortableHeader></th>
                  <th className="py-2.5 px-3"><SortableHeader sort={sort} k="employee">{t('intelligence.employee', { defaultValue: 'Employee' })}</SortableHeader></th>
                  <th className="py-2.5 px-3"><SortableHeader sort={sort} k="action">{t('decisions.action', { defaultValue: 'Action' })}</SortableHeader></th>
                  <th className="py-2.5 px-3"><SortableHeader sort={sort} k="status">{t('decisions.status', { defaultValue: 'Status' })}</SortableHeader></th>
                  <th className="py-2.5 px-3"><SortableHeader sort={sort} k="assignee">{t('decisions.assignee', { defaultValue: 'Assignee' })}</SortableHeader></th>
                  <th className="py-2.5 px-3"><SortableHeader sort={sort} k="due">{t('decisions.due', { defaultValue: 'Due' })}</SortableHeader></th>
                  <th className="py-2.5 px-3"><SortableHeader sort={sort} k="steps">{t('decisions.steps', { defaultValue: 'Steps' })}</SortableHeader></th>
                  <th className="py-2.5 pl-3" />
                </tr>
              </thead>
              <tbody>
                {sorted.map(a => {
                  const meta = STATUS_META[a.status] || STATUS_META.pending;
                  const SIcon = meta.icon;
                  const overdue = isOverdue(a);
                  return (
                    <tr key={a.id} className="border-b last:border-0 hover:bg-muted/40 cursor-pointer" onClick={() => { setOpen(a); setUpdateStatus(a.status); setUpdateAssignee(a.assignee_user_id || ''); setUpdateDueDate(a.due_date ? new Date(a.due_date).toISOString().slice(0,10) : ''); }} data-testid={`dec-row-${a.id}`}>
                      <td className="py-3 pr-3 text-xs text-muted-foreground tabular-nums">{fmtDate(a.created_at)}</td>
                      <td className="py-3 px-3">
                        <div className="font-medium">{a.user?.name || '—'}</div>
                        <div className="text-xs text-muted-foreground">{tData(a.department?.name) || '—'}</div>
                      </td>
                      <td className="py-3 px-3">
                        <div className="font-medium">{tData(a.suggestion_title)}</div>
                        <div className="text-xs text-muted-foreground capitalize">{tData((a.suggestion_type || '').replace('_',' '))}</div>
                      </td>
                      <td className="py-3 px-3">
                        <Badge className={`${meta.tone} text-xs gap-1`}><SIcon className="h-3 w-3" />{t(`decisions.${a.status}`, { defaultValue: meta.label })}</Badge>
                      </td>
                      <td className="py-3 px-3">
                        {a.assignee_name ? (
                          <span className="text-xs flex items-center gap-1"><Users className="h-3 w-3 text-muted-foreground" />{a.assignee_name}</span>
                        ) : <span className="text-xs italic text-muted-foreground">{t('decisions.unassigned', { defaultValue: '—' })}</span>}
                      </td>
                      <td className="py-3 px-3 text-xs tabular-nums">
                        {a.due_date ? (
                          overdue
                            ? <Badge className="bg-destructive/10 text-destructive border-destructive/30 gap-1 text-xs"><AlertCircle className="h-3 w-3" />{fmtDate(a.due_date)}</Badge>
                            : <span>{fmtDate(a.due_date)}</span>
                        ) : <span className="italic text-muted-foreground">—</span>}
                      </td>
                      <td className="py-3 px-3 text-xs tabular-nums">{(a.audit_trail || []).length}</td>
                      <td className="py-3 pl-3 text-right text-xs text-muted-foreground">{fmtTime(a.updated_at)}</td>
                    </tr>
                  );
                })}
                {!sorted.length && (
                  <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">{t('decisions.empty', { defaultValue: 'No decisions logged yet — open the TalentIntelligence dashboard, drill into an employee, and mark a suggestion as taken.' })}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Detail / update dialog */}
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-2xl">
          {open && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl">{tData(open.suggestion_title)}</DialogTitle>
                <DialogDescription>{open.user?.name} · {tData(open.department?.name) || '—'} · {tData((open.suggestion_type || '').replace('_',' '))}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm leading-relaxed text-foreground/90">{tData(open.suggestion_detail)}</p>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t('decisions.changeStatus', { defaultValue: 'Status' })}</Label>
                    <Select value={updateStatus || open.status} onValueChange={setUpdateStatus}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS_META).map(([k, m]) => <SelectItem key={k} value={k}>{t(`decisions.${k}`, { defaultValue: m.label })}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t('decisions.assignee', { defaultValue: 'Assignee' })}</Label>
                    <Select value={updateAssignee || '__none__'} onValueChange={setUpdateAssignee}>
                      <SelectTrigger data-testid="dec-assignee-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">{t('decisions.unassigned', { defaultValue: 'Unassigned' })}</SelectItem>
                        {assignees.map(u => <SelectItem key={u.id} value={u.id}>{u.name} <span className="text-muted-foreground text-xs">· {u.role}</span></SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t('decisions.due', { defaultValue: 'Due date' })}</Label>
                    <Input type="date" value={updateDueDate === '__clear__' ? '' : updateDueDate} onChange={(e) => setUpdateDueDate(e.target.value)} data-testid="dec-due-input" />
                    {open.due_date && (
                      <Button size="sm" variant="ghost" className="h-6 text-xs mt-1 text-destructive" onClick={() => setUpdateDueDate('__clear__')}>{t('decisions.clearDue', { defaultValue: 'Clear due date' })}</Button>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t('decisions.note', { defaultValue: 'Add a note (optional)' })}</Label>
                    <Input value={updateNote} onChange={(e)=>setUpdateNote(e.target.value)} placeholder={t('decisions.notePh', { defaultValue: 'Reason / next step / owner…' })} />
                  </div>
                </div>

                <div>
                  <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2 flex items-center gap-1.5"><History className="h-3 w-3" /> {t('decisions.timeline', { defaultValue: 'Timeline' })}</p>
                  <div className="space-y-2 border-l-2 border-border pl-4">
                    {(open.audit_trail || []).map((e, i) => (
                      <div key={i} className="text-xs">
                        <div className="font-medium">
                          {e.from_status ? (
                            <>{t(`decisions.${e.from_status}`, { defaultValue: STATUS_META[e.from_status]?.label })} → {t(`decisions.${e.to_status}`, { defaultValue: STATUS_META[e.to_status]?.label })}</>
                          ) : (
                            <>{t('decisions.created_event', { defaultValue: 'Created' })} → {t(`decisions.${e.to_status}`, { defaultValue: STATUS_META[e.to_status]?.label })}</>
                          )}
                        </div>
                        <div className="text-muted-foreground">{fmtTime(e.timestamp)} · {e.actor_role}</div>
                        {e.note && <div className="italic">"{e.note}"</div>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={()=>setOpen(null)}>{t('common.close', { defaultValue: 'Close' })}</Button>
                <Button onClick={submitUpdate} data-testid="dec-update-btn">{t('common.save', { defaultValue: 'Save' })}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function Label({ children, className = '', ...rest }) {
  return <p className={`text-xs font-medium mb-1 ${className}`} {...rest}>{children}</p>;
}
